/**
 * A minimal, dependency-free .xlsx writer.
 *
 * Why hand-rolled: the export needs a REAL Excel workbook (not a CSV that
 * Excel merely tolerates), and this workspace cannot pull SheetJS from npm.
 * An .xlsx is just a zip of five small XML parts; writing them ourselves is
 * ~150 lines, has zero dependencies, and produces a file Excel, Numbers and
 * LibreOffice all open without warnings.
 *
 * Scope on purpose: one sheet, every cell an inline string, and a SMALL
 * fixed style table (bold + a handful of row-fill colours) - just enough
 * for a readable report. No formulas, no shared strings.
 */

const encoder = new TextEncoder();

// ---------------------------------------------------------------- zip (store)

/** CRC-32, the one checksum the zip format demands. Table built once. */
let CRC_TABLE: Uint32Array | null = null;

function crcTable(): Uint32Array {
  if (CRC_TABLE) return CRC_TABLE;
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  CRC_TABLE = table;
  return table;
}

function crc32(data: Uint8Array): number {
  const table = crcTable();
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc = (crc >>> 8) ^ table[(crc ^ data[i]) & 0xff];
  }
  return (crc ^ 0xffffffff) >>> 0;
}

interface ZipEntry {
  name: string;
  data: Uint8Array;
}

/**
 * Build a zip with STORED (uncompressed) entries - valid per the spec, and
 * skipping deflate keeps this file dependency-free. Excel accepts it.
 */
function zip(entries: ZipEntry[]): Uint8Array {
  const now = new Date();
  const dosTime =
    (now.getHours() << 11) | (now.getMinutes() << 5) | (now.getSeconds() >> 1);
  const dosDate =
    ((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();

  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const crc = crc32(entry.data);

    const local = new Uint8Array(30 + name.length + entry.data.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true); // local file header signature
    lv.setUint16(4, 20, true); // version needed
    lv.setUint16(6, 0, true); // flags
    lv.setUint16(8, 0, true); // method: stored
    lv.setUint16(10, dosTime, true);
    lv.setUint16(12, dosDate, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, entry.data.length, true); // compressed size (= raw)
    lv.setUint32(22, entry.data.length, true); // uncompressed size
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true); // extra length
    local.set(name, 30);
    local.set(entry.data, 30 + name.length);
    locals.push(local);

    const central = new Uint8Array(46 + name.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true); // central directory signature
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0, true); // flags
    cv.setUint16(10, 0, true); // method: stored
    cv.setUint16(12, dosTime, true);
    cv.setUint16(14, dosDate, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, entry.data.length, true);
    cv.setUint32(24, entry.data.length, true);
    cv.setUint16(28, name.length, true);
    // extra, comment, disk, internal attrs, external attrs: all zero
    cv.setUint32(42, offset, true); // local header offset
    central.set(name, 46);
    centrals.push(central);

    offset += local.length;
  }

  const centralSize = centrals.reduce((sum, c) => sum + c.length, 0);
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true); // end of central directory signature
  ev.setUint16(8, entries.length, true); // entries on this disk
  ev.setUint16(10, entries.length, true); // entries total
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true); // central directory offset

  const total = offset + centralSize + eocd.length;
  const out = new Uint8Array(total);
  let at = 0;
  for (const part of [...locals, ...centrals, eocd]) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

// ---------------------------------------------------------------- xlsx parts

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    // Control characters are illegal in XML 1.0 and Excel refuses the file.
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
}

/** 0 -> A, 25 -> Z, 26 -> AA ... the spreadsheet column naming scheme. */
function columnName(index: number): string {
  let name = '';
  let n = index;
  do {
    name = String.fromCharCode(65 + (n % 26)) + name;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return name;
}

// --------------------------------------------------------------- styling

/** ARGB fills, index-aligned with XLSX_FILL below. Soft pastel tints so
 *  black text stays readable across the whole row. */
const FILL_COLORS = [
  'FFE2E8F0', // SLATE  - Not started
  'FFFEF3C7', // AMBER  - In progress
  'FFFEE2E2', // RED    - Blocked
  'FFD1FAE5', // GREEN  - Completed
  'FFF1F5F9', // GRAY   - On hold / cancelled
  'FFEDE9FE', // PURPLE - engagements and anything violet on screen
  'FFE0F2FE', // SKY    - Rescheduled meetings
];

export const XLSX_FILL = {
  SLATE: 0, AMBER: 1, RED: 2, GREEN: 3, GRAY: 4, PURPLE: 5, SKY: 6,
} as const;

/** Dark header band, drawn with white bold text. */
const HEADER_BG = 'FF0F172A';

/**
 * Resolve a cell style index for the fixed style table in stylesXml():
 * plain, bold, or one of the fills - each with and without bold. Every
 * style carries a thin light border, so cells read as separated.
 */
export function cellStyle(fill?: number, bold = false): number {
  if (fill === undefined) return bold ? 1 : 0;
  return 2 + fill * 2 + (bold ? 1 : 0);
}

/** The dark table-header style (white bold on slate-900). */
export const HEADER_STYLE = 2 + FILL_COLORS.length * 2;

function stylesXml(): string {
  const fonts =
    `<fonts count="3">` +
    `<font><sz val="11"/><name val="Calibri"/></font>` +
    `<font><b/><sz val="11"/><name val="Calibri"/></font>` +
    `<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>` +
    `</fonts>`;
  // Excel requires fills 0 (none) and 1 (gray125) before any custom fill.
  const customFills = [...FILL_COLORS, HEADER_BG];
  const fills =
    `<fills count="${2 + customFills.length}">` +
    `<fill><patternFill patternType="none"/></fill>` +
    `<fill><patternFill patternType="gray125"/></fill>` +
    customFills.map(
      (argb) =>
        `<fill><patternFill patternType="solid"><fgColor rgb="${argb}"/><bgColor indexed="64"/></patternFill></fill>`,
    ).join('') +
    `</fills>`;
  // Border 1: thin slate-300 on all four sides - the visible cell grid.
  const thin = `<left style="thin"><color rgb="FFCBD5E1"/></left><right style="thin"><color rgb="FFCBD5E1"/></right><top style="thin"><color rgb="FFCBD5E1"/></top><bottom style="thin"><color rgb="FFCBD5E1"/></bottom>`;
  const borders = `<borders count="2"><border/><border>${thin}</border></borders>`;
  const align = `<alignment vertical="center" wrapText="0"/>`;
  const xf = (fontId: number, fillId: number) =>
    `<xf fontId="${fontId}" fillId="${fillId}" borderId="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1">${align}</xf>`;
  // cellXfs mirror cellStyle(): 0 plain, 1 bold, then per fill (plain, bold),
  // then HEADER_STYLE last.
  const xfs = [xf(0, 0), xf(1, 0)];
  FILL_COLORS.forEach((_, i) => {
    xfs.push(xf(0, i + 2));
    xfs.push(xf(1, i + 2));
  });
  xfs.push(xf(2, FILL_COLORS.length + 2)); // HEADER_STYLE
  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    fonts +
    fills +
    borders +
    `<cellStyleXfs count="1"><xf fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
    `<cellXfs count="${xfs.length}">${xfs.join('')}</cellXfs>` +
    `</styleSheet>`
  );
}

/** Per-cell style indexes (from cellStyle), same shape as the cell grid.
 *  A missing row or cell means "unstyled". */
export type StyleGrid = (number | undefined)[][];

export interface SheetOptions {
  /** Column widths in Excel "characters"; index-aligned with the columns. */
  colWidths?: number[];
  /** Keep the header row visible while scrolling. */
  freezeHeader?: boolean;
  /** Filter dropdowns across the header row. */
  autoFilter?: boolean;
}

function sheetXml(rows: string[][], styles?: StyleGrid, options: SheetOptions = {}): string {
  const body = rows
    .map((cells, r) => {
      const cols = cells
        .map((cell, c) => {
          const s = styles?.[r]?.[c];
          const sAttr = s ? ` s="${s}"` : '';
          return `<c r="${columnName(c)}${r + 1}"${sAttr} t="inlineStr"><is><t xml:space="preserve">${xmlEscape(cell)}</t></is></c>`;
        })
        .join('');
      return `<row r="${r + 1}">${cols}</row>`;
    })
    .join('');
  const views = options.freezeHeader
    ? `<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>`
    : '';
  const colsXml = options.colWidths?.length
    ? `<cols>${options.colWidths
        .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
        .join('')}</cols>`
    : '';
  const colCount = rows[0]?.length ?? 0;
  const filter =
    options.autoFilter && rows.length > 1 && colCount > 0
      ? `<autoFilter ref="A1:${columnName(colCount - 1)}${rows.length}"/>`
      : '';
  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    views +
    colsXml +
    `<sheetData>${body}</sheetData>` +
    filter +
    `</worksheet>`
  );
}

/**
 * Build a one-sheet workbook. `rows` is the full grid, header row included;
 * every cell is written as text, so nothing is ever mangled the way Excel
 * mangles CSV dates and long numbers.
 */
export function makeXlsx(
  sheetName: string,
  rows: string[][],
  styles?: StyleGrid,
  options?: SheetOptions,
): Blob {
  // Sheet names: max 31 chars, and []:*?/\ are forbidden by Excel.
  const safeName = xmlEscape(sheetName.replace(/[[\]:*?/\\]/g, ' ').slice(0, 31) || 'Sheet1');

  const contentTypes =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
    `<Default Extension="xml" ContentType="application/xml"/>` +
    `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
    `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>` +
    `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
    `</Types>`;

  const rootRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
    `</Relationships>`;

  const workbook =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ` +
    `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
    `<sheets><sheet name="${safeName}" sheetId="1" r:id="rId1"/></sheets></workbook>`;

  const workbookRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>` +
    `<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    `</Relationships>`;

  const bytes = zip([
    { name: '[Content_Types].xml', data: encoder.encode(contentTypes) },
    { name: '_rels/.rels', data: encoder.encode(rootRels) },
    { name: 'xl/workbook.xml', data: encoder.encode(workbook) },
    { name: 'xl/_rels/workbook.xml.rels', data: encoder.encode(workbookRels) },
    { name: 'xl/styles.xml', data: encoder.encode(stylesXml()) },
    { name: 'xl/worksheets/sheet1.xml', data: encoder.encode(sheetXml(rows, styles, options)) },
  ]);

  return new Blob([bytes.buffer as ArrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/** Hand the workbook to the browser as a download. */
export function downloadXlsx(
  filename: string,
  sheetName: string,
  rows: string[][],
  styles?: StyleGrid,
  options?: SheetOptions,
): void {
  const url = URL.createObjectURL(makeXlsx(sheetName, rows, styles, options));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Give the click a tick to start before the URL is torn down.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
